import {useRef, useState} from "react";
import {Client, IMessage, StompSubscription} from "@stomp/stompjs";
import SockJS from "sockjs-client";
import Cookies from "js-cookie";
import {ChatModel} from "@/app/api/chat";
import {MessageModel} from "@/app/api/messages";
import {CookiesKey} from "@/app/utils/cookies";

type MessageCallback = (msg: MessageModel) => void;
type ChatCallback = (chat: ChatModel) => void;

const useChatSocket = () => {
  const WS_URL = "https://back.padelmatchapp.com/ws";
  const stompClient = useRef<Client | null>(null);
  const chatSub = useRef<StompSubscription | null>(null);
  const chatsListSub = useRef<StompSubscription | null>(null);
  const [connected, setConnected] = useState(false);

  const connect = (onConnect?: () => void, onError?: (err: any) => void) => {
    const socket = new SockJS(WS_URL);
    stompClient.current = new Client({
      webSocketFactory: () => socket,
      // debug: (str) => console.log(str),
      reconnectDelay: 5000,
      // connectHeaders: {
      //   Authorization: `Bearer ${Cookies.get(CookiesKey.token)}`,
      // },
      onConnect: (frame) => {
        onConnect && onConnect();
        setConnected(true);
      },
      onStompError: (frame) => {
        console.error("Broker reported error: " + frame.headers["message"]);
        console.error("Additional details: " + frame.body);
        onError && onError(frame);
        setConnected(false);
      },
    });
    stompClient.current.activate();
  };

  const disconnect = () => {
    if (chatSub.current) {
      chatSub.current.unsubscribe();
      chatSub.current = null;
    }
    if (chatsListSub.current) {
      chatsListSub.current.unsubscribe();
      chatsListSub.current = null;
    }
    if (stompClient.current) {
      stompClient.current.deactivate();
      stompClient.current = null;
    }
  };

  const subscribeToChatUpdates = (chatId: number, onMessage: MessageCallback) => {
    if (chatSub.current) {
      chatSub.current.unsubscribe();
      chatSub.current = null;
    }
    if (stompClient.current && stompClient.current.connected) {
      chatSub.current = stompClient.current.subscribe(`/topic/chat/${chatId}`, (message: IMessage) => {
        const msg: MessageModel = JSON.parse(message.body);
        onMessage(msg);
      });
    } else {
      console.warn("STOMP client is not connected yet!");
    }
  };

  const subscribeToChatListUpdates = (userId: number, onChat: ChatCallback) => {
    if (chatsListSub.current) {
      chatsListSub.current.unsubscribe();
      chatsListSub.current = null;
    }
    if (stompClient.current && stompClient.current.connected) {
      chatsListSub.current = stompClient.current.subscribe(`/topic/userChats/${userId}`, (chat: IMessage) => {
        const ch: ChatModel = JSON.parse(chat.body);
        onChat(ch);
      });
    } else {
      console.warn("STOMP client is not connected yet!");
    }
  };

  return {
    connect,
    disconnect,
    subscribeToChatUpdates,
    subscribeToChatListUpdates,
    connected
  };
};

export default useChatSocket;
